/**
 * Gépi hibakódok — a spec 15.5 § #5 követeli meg: "Az API egységes, géppel
 * feldolgozható hibakódokat adjon."
 *
 * A `code` a KONTRAKTUS: sosem fordítjuk le, sosem nevezzük át. A `title` és
 * `detail` magyar, megjelenítésre kész. Az Android kliens ezen az enumon
 * kimerítően `when`-elhet, és van egy teszt, ami buktatja a buildet, ha egy
 * kódhoz nincs magyar szöveg.
 */
export const ErrorCode = {
    // --- 401 Hitelesítés ---
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    INVALID_TENANT: 'INVALID_TENANT',
    // --- 403 Jogosultság ---
    FORBIDDEN: 'FORBIDDEN',
    USER_DISABLED: 'USER_DISABLED',
    // --- 400 Kérésformátum ---
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
    BARCODE_FORMAT_INVALID: 'BARCODE_FORMAT_INVALID',
    BARCODE_TYPE_MISMATCH: 'BARCODE_TYPE_MISMATCH',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    BULK_TOO_LARGE: 'BULK_TOO_LARGE',
    // --- 404 Nem található ---
    /** Nem létező API-útvonal. Fejlesztői hiba, nem üzleti. */
    ROUTE_NOT_FOUND: 'ROUTE_NOT_FOUND',
    FOLDER_NOT_FOUND: 'FOLDER_NOT_FOUND',
    LOCATION_NOT_FOUND: 'LOCATION_NOT_FOUND',
    PALLET_NOT_FOUND: 'PALLET_NOT_FOUND',
    PALLET_LEVEL_NOT_FOUND: 'PALLET_LEVEL_NOT_FOUND',
    /**
     * A POLC nem rendelkezik ezzel a szinttel (E1).
     *
     * SZÁNDÉKOSAN nem a `PALLET_LEVEL_NOT_FOUND`-ot hasznosítjuk újra: a szint
     * a D fázisban lokáció-szintű fogalom lett, a hibaüzenete viszont
     * raklapszintről beszél. Egy targoncást „a raklapnak nincs ilyen szintje"
     * üzenettel a raklapot vizsgálni küldeni, amikor a POLC szintje hiányzik,
     * pontosan az a fajta félrevezetés, amit a D0a-ban javítottunk.
     */
    SHELF_LEVEL_NOT_FOUND: 'SHELF_LEVEL_NOT_FOUND',
    IMAGE_NOT_FOUND: 'IMAGE_NOT_FOUND',
    RECOGNITION_NOT_FOUND: 'RECOGNITION_NOT_FOUND',
    BATCH_NOT_FOUND: 'BATCH_NOT_FOUND',
    /** F1: nincs ilyen cég a katalógusban. */
    COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
    /** G2: nincs ilyen nyomtató a törzsadatban. */
    PRINTER_NOT_FOUND: 'PRINTER_NOT_FOUND',
    /** G4: az elsődleges kép nem törölhető — előbb másikat kell azzá tenni. */
    IMAGE_IS_PRIMARY: 'IMAGE_IS_PRIMARY',
    /** G3: nincs ilyen kihagyandó minta. */
    SPINE_IGNORE_PATTERN_NOT_FOUND: 'SPINE_IGNORE_PATTERN_NOT_FOUND',
    // --- 409 Ütközés ---
    FOLDER_BARCODE_EXISTS: 'FOLDER_BARCODE_EXISTS',
    LOCATION_BARCODE_EXISTS: 'LOCATION_BARCODE_EXISTS',
    CONCURRENT_MODIFICATION: 'CONCURRENT_MODIFICATION',
    REQUEST_IN_PROGRESS: 'REQUEST_IN_PROGRESS',
    PALLET_LEVEL_CLOSED: 'PALLET_LEVEL_CLOSED',
    PALLET_LEVEL_ALREADY_OPEN: 'PALLET_LEVEL_ALREADY_OPEN',
    PALLET_LEVEL_ALREADY_CLOSED: 'PALLET_LEVEL_ALREADY_CLOSED',
    FOLDER_AT_OTHER_LOCATION: 'FOLDER_AT_OTHER_LOCATION',
    IMAGE_DUPLICATE_CHECKSUM: 'IMAGE_DUPLICATE_CHECKSUM',
    RECOGNITION_ALREADY_RUNNING: 'RECOGNITION_ALREADY_RUNNING',
    /** G1: a polcszint le van zárva — betárolni nem lehet, kivenni igen. */
    SHELF_LEVEL_LOCKED: 'SHELF_LEVEL_LOCKED',
    /** G1: csak a LEGFELSŐ, ÜRES szint törölhető. */
    LEVEL_NOT_REMOVABLE: 'LEVEL_NOT_REMOVABLE',
    /** G1: lezárni csak polcszintet lehet — a raklapszintnek nincs állapota. */
    LEVEL_LOCK_NOT_APPLICABLE: 'LEVEL_LOCK_NOT_APPLICABLE',
    /** F1: a cégkód már foglalt. */
    COMPANY_CODE_EXISTS: 'COMPANY_CODE_EXISTS',
    /** G2: ez a PrintNode-azonosító már fel van véve. */
    PRINTER_ALREADY_EXISTS: 'PRINTER_ALREADY_EXISTS',
    /** G3: ez a minta már szerepel a listán. */
    SPINE_IGNORE_PATTERN_EXISTS: 'SPINE_IGNORE_PATTERN_EXISTS',
    /** F1: a névváltozat már MÁS céghez tartozik — egy alias csak egy céget jelenthet. */
    COMPANY_ALIAS_TAKEN: 'COMPANY_ALIAS_TAKEN',
    // --- 422 Üzleti szabály sérülés ---
    IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
    PALLET_LEVEL_REQUIRED: 'PALLET_LEVEL_REQUIRED',
    PALLET_LEVEL_FORBIDDEN: 'PALLET_LEVEL_FORBIDDEN',
    PALLET_LEVEL_EXCEEDS_MAX: 'PALLET_LEVEL_EXCEEDS_MAX',
    LOCATION_INACTIVE: 'LOCATION_INACTIVE',
    LOCATION_TYPE_IMMUTABLE: 'LOCATION_TYPE_IMMUTABLE',
    /** F1: a „Besorolatlan” nem deaktiválható — ez a spine_company DEFAULT-ja. */
    COMPANY_UNCLASSIFIED_IMMUTABLE: 'COMPANY_UNCLASSIFIED_IMMUTABLE',
    PALLET_NOT_ACTIVE: 'PALLET_NOT_ACTIVE',
    TARGET_SAME_AS_SOURCE_PALLET: 'TARGET_SAME_AS_SOURCE_PALLET',
    PALLET_EMPTY: 'PALLET_EMPTY',
    PALLET_LEVEL_EMPTY: 'PALLET_LEVEL_EMPTY',
    CANNOT_DEACTIVATE_NON_EMPTY_LOCATION: 'CANNOT_DEACTIVATE_NON_EMPTY_LOCATION',
    DEFAULT_STAGING_REQUIRED: 'DEFAULT_STAGING_REQUIRED',
    RECOGNITION_NOT_RETRIABLE: 'RECOGNITION_NOT_RETRIABLE',
    /** A vonalkódot sosem osztottuk ki (B9) — elgépelés, idegen vagy megkerült címke. */
    LABEL_NOT_ALLOCATED: 'LABEL_NOT_ALLOCATED',
    /** A kiosztott vonalkód selejtezve lett (B9). */
    LABEL_VOIDED: 'LABEL_VOIDED',
    /** Elfogytak a szabad MAP- sorszámok (B9, 99999-es plafon). */
    LABEL_SEQUENCE_EXHAUSTED: 'LABEL_SEQUENCE_EXHAUSTED',
    // --- 5xx ---
    /** Az adatbázis-szintű invariáns sérült. Riasztást vált ki. */
    INVARIANT_VIOLATION: 'INVARIANT_VIOLATION',
    UPSTREAM_UNAVAILABLE: 'UPSTREAM_UNAVAILABLE',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
/** HTTP státusz minden kódhoz. */
export const ERROR_STATUS = {
    UNAUTHENTICATED: 401,
    TOKEN_EXPIRED: 401,
    INVALID_TENANT: 401,
    FORBIDDEN: 403,
    USER_DISABLED: 403,
    VALIDATION_FAILED: 400,
    IDEMPOTENCY_KEY_REQUIRED: 400,
    BARCODE_FORMAT_INVALID: 400,
    BARCODE_TYPE_MISMATCH: 400,
    PAYLOAD_TOO_LARGE: 413,
    BULK_TOO_LARGE: 400,
    ROUTE_NOT_FOUND: 404,
    FOLDER_NOT_FOUND: 404,
    LOCATION_NOT_FOUND: 404,
    PALLET_NOT_FOUND: 404,
    PALLET_LEVEL_NOT_FOUND: 404,
    SHELF_LEVEL_NOT_FOUND: 404,
    IMAGE_NOT_FOUND: 404,
    RECOGNITION_NOT_FOUND: 404,
    BATCH_NOT_FOUND: 404,
    COMPANY_NOT_FOUND: 404,
    PRINTER_NOT_FOUND: 404,
    IMAGE_IS_PRIMARY: 422,
    SPINE_IGNORE_PATTERN_NOT_FOUND: 404,
    FOLDER_BARCODE_EXISTS: 409,
    LOCATION_BARCODE_EXISTS: 409,
    CONCURRENT_MODIFICATION: 409,
    REQUEST_IN_PROGRESS: 409,
    PALLET_LEVEL_CLOSED: 409,
    PALLET_LEVEL_ALREADY_OPEN: 409,
    PALLET_LEVEL_ALREADY_CLOSED: 409,
    FOLDER_AT_OTHER_LOCATION: 409,
    IMAGE_DUPLICATE_CHECKSUM: 409,
    RECOGNITION_ALREADY_RUNNING: 409,
    SHELF_LEVEL_LOCKED: 409,
    LEVEL_NOT_REMOVABLE: 409,
    COMPANY_CODE_EXISTS: 409,
    PRINTER_ALREADY_EXISTS: 409,
    SPINE_IGNORE_PATTERN_EXISTS: 409,
    COMPANY_ALIAS_TAKEN: 409,
    IDEMPOTENCY_KEY_REUSED: 422,
    PALLET_LEVEL_REQUIRED: 422,
    PALLET_LEVEL_FORBIDDEN: 422,
    PALLET_LEVEL_EXCEEDS_MAX: 422,
    LOCATION_INACTIVE: 422,
    LOCATION_TYPE_IMMUTABLE: 422,
    LEVEL_LOCK_NOT_APPLICABLE: 422,
    COMPANY_UNCLASSIFIED_IMMUTABLE: 422,
    PALLET_NOT_ACTIVE: 422,
    TARGET_SAME_AS_SOURCE_PALLET: 422,
    PALLET_EMPTY: 422,
    PALLET_LEVEL_EMPTY: 422,
    CANNOT_DEACTIVATE_NON_EMPTY_LOCATION: 422,
    DEFAULT_STAGING_REQUIRED: 422,
    RECOGNITION_NOT_RETRIABLE: 422,
    LABEL_NOT_ALLOCATED: 422,
    LABEL_VOIDED: 422,
    LABEL_SEQUENCE_EXHAUSTED: 422,
    INVARIANT_VIOLATION: 500,
    UPSTREAM_UNAVAILABLE: 503,
    INTERNAL_ERROR: 500,
};
/** Magyar, megjelenítésre kész cím minden kódhoz. */
export const ERROR_TITLE_HU = {
    UNAUTHENTICATED: 'Nincs bejelentkezve.',
    TOKEN_EXPIRED: 'A munkamenet lejárt, jelentkezz be újra.',
    INVALID_TENANT: 'Ez a fiók nem tartozik a Persped szervezethez.',
    FORBIDDEN: 'Nincs jogosultságod ehhez a művelethez.',
    USER_DISABLED: 'A felhasználói fiók le van tiltva.',
    VALIDATION_FAILED: 'Hibás adatok.',
    IDEMPOTENCY_KEY_REQUIRED: 'Hiányzik az idempotencia-kulcs.',
    BARCODE_FORMAT_INVALID: 'Érvénytelen vonalkód-formátum.',
    BARCODE_TYPE_MISMATCH: 'Nem a várt típusú vonalkód.',
    PAYLOAD_TOO_LARGE: 'A küldött adat túl nagy.',
    BULK_TOO_LARGE: 'Túl sok tétel egyszerre.',
    ROUTE_NOT_FOUND: 'A kért végpont nem létezik.',
    FOLDER_NOT_FOUND: 'Nincs ilyen mappa.',
    LOCATION_NOT_FOUND: 'Nincs ilyen lokáció.',
    PALLET_NOT_FOUND: 'Nincs ilyen raklap.',
    PALLET_LEVEL_NOT_FOUND: 'Nincs ilyen raklapszint.',
    SHELF_LEVEL_NOT_FOUND: 'A polcnak nincs ilyen szintje.',
    IMAGE_NOT_FOUND: 'Nincs ilyen kép.',
    RECOGNITION_NOT_FOUND: 'Nincs ilyen felismerési eredmény.',
    BATCH_NOT_FOUND: 'Nincs ilyen munkamenet.',
    COMPANY_NOT_FOUND: 'Nincs ilyen cég.',
    PRINTER_NOT_FOUND: 'Nincs ilyen nyomtató.',
    IMAGE_IS_PRIMARY: 'Az elsődleges kép nem törölhető.',
    SPINE_IGNORE_PATTERN_NOT_FOUND: 'Nincs ilyen kihagyandó minta.',
    FOLDER_BARCODE_EXISTS: 'Ez a mappa már regisztrálva van.',
    LOCATION_BARCODE_EXISTS: 'Ez a lokációkód már foglalt.',
    CONCURRENT_MODIFICATION: 'Valaki közben módosította — töltsd újra.',
    REQUEST_IN_PROGRESS: 'Ez a művelet épp folyamatban van.',
    PALLET_LEVEL_CLOSED: 'A raklapszint le van zárva.',
    PALLET_LEVEL_ALREADY_OPEN: 'A raklapszint már nyitva van.',
    PALLET_LEVEL_ALREADY_CLOSED: 'A raklapszint már le van zárva.',
    FOLDER_AT_OTHER_LOCATION: 'A mappa jelenleg máshol van.',
    IMAGE_DUPLICATE_CHECKSUM: 'Ez a kép már fel van töltve.',
    RECOGNITION_ALREADY_RUNNING: 'A felismerés már fut.',
    SHELF_LEVEL_LOCKED: 'Ez a polcszint le van zárva.',
    LEVEL_NOT_REMOVABLE: 'Csak a legfelső, üres szint törölhető.',
    COMPANY_CODE_EXISTS: 'Ez a cégkód már foglalt.',
    PRINTER_ALREADY_EXISTS: 'Ez a nyomtató már fel van véve.',
    SPINE_IGNORE_PATTERN_EXISTS: 'Ez a minta már szerepel a listán.',
    COMPANY_ALIAS_TAKEN: 'Ez a névváltozat már másik céghez tartozik.',
    IDEMPOTENCY_KEY_REUSED: 'Ezt a kulcsot már más adattal használtuk.',
    PALLET_LEVEL_REQUIRED: 'Raklap célhelynél a szint megadása kötelező.',
    PALLET_LEVEL_FORBIDDEN: 'Nem raklap típusú lokációnál nem adható meg szint.',
    PALLET_LEVEL_EXCEEDS_MAX: 'A raklapnak nincs ennyi szintje.',
    LOCATION_INACTIVE: 'A lokáció inaktív.',
    LOCATION_TYPE_IMMUTABLE: 'A lokáció típusa nem módosítható.',
    LEVEL_LOCK_NOT_APPLICABLE: 'Lezárni csak polcszintet lehet.',
    COMPANY_UNCLASSIFIED_IMMUTABLE: 'A „Besorolatlan” nem deaktiválható.',
    PALLET_NOT_ACTIVE: 'A raklap nem aktív.',
    TARGET_SAME_AS_SOURCE_PALLET: 'A célhely nem lehet ugyanaz a raklap.',
    PALLET_EMPTY: 'A raklapon nincs mappa.',
    PALLET_LEVEL_EMPTY: 'Ezen a szinten nincs mappa.',
    CANNOT_DEACTIVATE_NON_EMPTY_LOCATION: 'A lokáció nem üres, nem deaktiválható.',
    DEFAULT_STAGING_REQUIRED: 'Kell egy alapértelmezett Előkészítő lokáció.',
    RECOGNITION_NOT_RETRIABLE: 'Ez a felismerés nem indítható újra.',
    LABEL_NOT_ALLOCATED: 'Ez a vonalkód nincs lefoglalva.',
    LABEL_VOIDED: 'Ez a címke érvénytelenítve lett.',
    LABEL_SEQUENCE_EXHAUSTED: 'Elfogytak a szabad mappa-sorszámok.',
    INVARIANT_VIOLATION: 'Belső adatkonzisztencia-hiba.',
    UPSTREAM_UNAVAILABLE: 'Egy külső szolgáltatás nem érhető el.',
    INTERNAL_ERROR: 'Váratlan szerverhiba.',
};
//# sourceMappingURL=errors.js.map